import React, { useEffect, useMemo, useRef, useState } from "react"
import styles from "./ScrollingImageGrid.module.scss"
import images from "../../../utils/images"
import Svg from "../../../utils/svg"
import { useAppState } from "../../../context/appStateContext"
import useQueryParams from "../../../hooks/useQueryParams"

const imageCount = 12

function shuffle(array: string[]) {
  return array.sort(() => Math.random() - 0.5)
}

interface Props {
  containerClass?: string
}

interface ImageRowProps {
  offsetTop?: boolean
  isLeftmost?: boolean
  isRightmost?: boolean
  imageIndex: number
  isBlurred: boolean
}

const ImageRow = ({
  offsetTop,
  imageIndex,
  isBlurred,
  isLeftmost,
  isRightmost,
}: ImageRowProps) => {
  const isAlternateImageSet = useQueryParams().alternateImageSet

  const imageClasses = useMemo(() => {
    let result: string[] = Array.from(
      { length: imageCount },
      (_, i) => images.animatedPaywall[`img${i + 1}`].class
    )

    if (isAlternateImageSet) {
      const alternateIndices = [0, 1, 11, 3, 5, 7, 9]
      for (let i = 0; i < alternateIndices.length; i++) {
        console.log(`img${13 + i + 1}`)
        result[alternateIndices[i]] =
          images.animatedPaywall[`img${13 + i}`].class
      }
    }

    return result
  }, [])

  const index = imageIndex % imageClasses.length

  let horizontalOffset = 30

  if (window.innerWidth > 600) {
    horizontalOffset = 0
  }

  return (
    <div
      data-index={imageIndex}
      className={styles.imageContainer}
      style={{
        marginBottom: offsetTop ? "30px" : "0",
        marginTop: offsetTop ? "-30px" : "0",
      }}
    >
      <div
        className={`${styles.image} ${imageClasses[index]} ${
          isBlurred ? styles.blur : ""
        }`}
      />
      {isBlurred && (
        <div
          className={styles.lockContainer}
          style={{
            marginLeft: isLeftmost
              ? `${horizontalOffset}px`
              : isRightmost
              ? `-${horizontalOffset}px`
              : `0`,
          }}
        >
          <Svg.featureIcons.SecurityPlus className={`${styles.lockIcon}`} />
        </div>
      )}
    </div>
  )
}

const ScrollingImageGrid = ({ containerClass }: Props) => {
  const shouldAnimate = useAppState().state.productData?.loaded == true
  const [itemBlurred, setItemBlurred] = useState<boolean[]>([])
  const containerRef = useRef<HTMLDivElement>(null)

  const rowCount = 100
  const totalItems = rowCount * 3

  // ensure blurred array has stable length
  useEffect(() => {
    setItemBlurred(Array(totalItems).fill(false))
  }, [totalItems])

  // poll every 300 ms for items that scrolled past the viewport top
  useEffect(() => {
    const interval = setInterval(() => {
      if (!containerRef.current) return

      const nodes =
        containerRef.current.querySelectorAll<HTMLDivElement>("[data-index]")

      if (nodes.length === 0) return

      const toBlur: number[] = []
      const toUnblur: number[] = []

      nodes.forEach((node) => {
        const rect = node.getBoundingClientRect()
        const attr = node.getAttribute("data-index")
        if (attr === null) return
        const index = parseInt(attr, 10)

        if (rect.top < -20 && shouldAnimate) {
          toBlur.push(index)
        } else if (rect.top >= -20) {
          toUnblur.push(index)
        }
      })

      if (toBlur.length === 0 && toUnblur.length === 0) return

      setItemBlurred((prev) => {
        let changed = false
        const next = [...prev]

        toBlur.forEach((idx) => {
          if (!next[idx]) {
            next[idx] = true
            changed = true
          }
        })

        toUnblur.forEach((idx) => {
          if (next[idx]) {
            next[idx] = false
            changed = true
          }
        })

        return changed ? next : prev
      })
    }, 300)

    return () => clearInterval(interval)
  }, [shouldAnimate])

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${containerClass || ""} ${
        shouldAnimate ? styles.animate : ""
      }`}
    >
      {Array.from({ length: 3 }).map((_, columnIndex) => (
        <div key={columnIndex} className={styles.column}>
          {Array.from({ length: rowCount }).map((_, rowIndex) => {
            const index = rowIndex * 3 + columnIndex
            const isBlurred = itemBlurred[index] || false
            return (
              <ImageRow
                key={index}
                imageIndex={index}
                offsetTop={columnIndex % 2 !== 0}
                isBlurred={isBlurred}
                isLeftmost={columnIndex % 3 == 0}
                isRightmost={columnIndex % 3 == 2}
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default ScrollingImageGrid
